import React from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' }
];

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    // Taalkeuze opslaan op het profiel (voor e-mails en de betaalpagina)
    axios.put('/user/profile', { language: languageCode }).catch(() => {});
    // Save to localStorage for persistence
    localStorage.setItem('i18nextLng', languageCode);
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  if (variant === 'brand') {
    return (
      <Select value={i18n.language} onValueChange={handleLanguageChange}>
        <SelectTrigger
          className="h-auto w-auto gap-1.5 border-0 bg-transparent p-0 font-poppins text-[clamp(15px,1vw,18px)] font-semibold leading-[15px] text-brand-navy shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-100"
          data-testid="language-switcher"
          aria-label={`Language: ${currentLanguage.name}`}
        >
          <img src="/brand/icon-language.svg" alt="" aria-hidden className="h-5 w-5" />
          <span className="text-base leading-none">{currentLanguage.flag}</span>
          <span>{currentLanguage.code.toUpperCase()}</span>
        </SelectTrigger>
        <SelectContent>
          {languages.map((language) => (
            <SelectItem key={language.code} value={language.code} data-testid={`language-option-${language.code}`}>
              <div className="flex items-center space-x-2">
                <span>{language.flag}</span>
                <span className="text-sm">{language.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (variant === 'compact' || variant === 'dark') {
    const dark = variant === 'dark';
    return (
      <Select value={i18n.language} onValueChange={handleLanguageChange}>
        <SelectTrigger
          className={`h-8 w-16 px-2 ${dark ? 'border-white/25 bg-transparent text-white hover:border-white/50' : 'border-zinc-300'}`}
          data-testid="language-switcher"
          aria-label={`Language: ${currentLanguage.name}`}
        >
          <SelectValue>
            <span className="text-sm">{currentLanguage.flag}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {languages.map((language) => (
            <SelectItem key={language.code} value={language.code} data-testid={`language-option-${language.code}`}>
              <div className="flex items-center space-x-2">
                <span>{language.flag}</span>
                <span className="text-sm">{language.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Select value={i18n.language} onValueChange={handleLanguageChange}>
      <SelectTrigger className="w-40 border-zinc-300 hover:border-brand" data-testid="language-switcher"
          aria-label={`Language: ${currentLanguage.name}`}>
        <div className="flex items-center space-x-2">
          <Globe className="w-4 h-4 text-zinc-500" />
          <SelectValue>
            <div className="flex items-center space-x-2">
              <span>{currentLanguage.flag}</span>
              <span className="text-sm font-medium">{currentLanguage.name}</span>
            </div>
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {languages.map((language) => (
          <SelectItem key={language.code} value={language.code} data-testid={`language-option-${language.code}`}>
            <div className="flex items-center space-x-3">
              <span className="text-lg">{language.flag}</span>
              <span className="font-medium">{language.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSwitcher;